import { useEffect } from 'react';
import {
  applyVerticalScroll,
  findConsumingHorizontalScroller,
  findVerticalScrollerForForward,
} from '../utils/touchScroll';

export { findConsumingHorizontalScroller };

/** Travel (px) before a pan is locked to an axis. */
export const HORIZONTAL_SWIPE_THRESHOLD = 8;

/** X must exceed Y by this ratio to lock as a history swipe instead of a scroll. */
export const AXIS_DOMINANCE_RATIO = 1.25;

export type TouchAxis = 'pending' | 'x' | 'y';

export function resolveTouchAxis(absX: number, absY: number): TouchAxis {
  if (absX <= HORIZONTAL_SWIPE_THRESHOLD && absY <= HORIZONTAL_SWIPE_THRESHOLD) {
    return 'pending';
  }
  return absX > absY * AXIS_DOMINANCE_RATIO ? 'x' : 'y';
}

export interface HistoryTouchMoveArgs {
  readonly target: EventTarget | null;
  readonly deltaX: number;
  readonly deltaY: number;
}

export function shouldPreventHistoryTouchMove(args: HistoryTouchMoveArgs): boolean {
  const absX = Math.abs(args.deltaX);
  const absY = Math.abs(args.deltaY);
  if (resolveTouchAxis(absX, absY) !== 'x') {
    return false;
  }
  return findConsumingHorizontalScroller(args.target, args.deltaX) === null;
}

/** Blocks OS/browser history swipe. Does not intercept popstate or chrome Back.
 *  Does not preventDefault on touchstart — that cancels native pan on tables/chips.
 *  Vertical pans that start on horizontal-only chip/table ports are forwarded to the
 *  parent scroller so Android does not eat the gesture. */
export function useDisableHistoryGestures(): void {
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    let lastY = 0;
    let axis: TouchAxis = 'pending';

    const onTouchStart = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      lastY = touch.clientY;
      axis = 'pending';
    };

    const onTouchMove = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      const moveDeltaY = touch.clientY - lastY;
      lastY = touch.clientY;

      if (axis === 'pending') {
        axis = resolveTouchAxis(Math.abs(deltaX), Math.abs(deltaY));
      }

      if (axis !== 'x') {
        if (axis === 'y' && event.cancelable) {
          const scroller = findVerticalScrollerForForward(event.target);
          if (scroller) {
            event.preventDefault();
            applyVerticalScroll(scroller, moveDeltaY);
          }
        }
        return;
      }

      if (
        event.cancelable &&
        findConsumingHorizontalScroller(event.target, deltaX) === null
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, []);
}
